import DatePicker from "react-datepicker";
import { useRef, useState } from "react";
import moment from "moment";
import React from "react";
import "react-datepicker/dist/react-datepicker.css";
import './DateRangePicker.scss';
import classNames from "classnames";
import { Button } from "react-bootstrap";
import { localizeCommon as localize } from "features/localization/localizeService";

export type DateRangePickerProps = {
    startDate: Date;
    endDate: Date;

    minStartDate?: Date;
    maxStartDate?: Date;
    minEndDate?: Date;
    maxEndDate?: Date;

    onRangeChanged(startDate: Date, endDate: Date): void;
    onValidationErrors(errors: string[]): any;

    showLabels?: boolean;
    showTime?: boolean;
    inline?: boolean;
    showApplyButton?: boolean;
    readOnly?: boolean;
};

type DateRangePickerState = { selectedStartDate?: Date, selectedEndDate?: Date, isValidStartDate: boolean, isValidEndDate: boolean};

const DateRangePicker: React.FC<DateRangePickerProps> = (props: DateRangePickerProps) => {
    const state = useRef<DateRangePickerState>({isValidStartDate: true, isValidEndDate: true})
    
    const INVALID_START_DATE: string = "Start Date should be earlier than End Date";
    const INVALID_END_DATE: string = "End Date should be later than Start Date";
    
    function validateDateFilter(changedDate: Date, isStartDate: boolean)
    {
        const newState = {...state.current};
        props.onValidationErrors([]);
        if(isStartDate)
        {
            newState.selectedStartDate = changedDate;
            newState.isValidEndDate = true;
            if (moment(changedDate).isSameOrAfter(state.current.selectedEndDate)) {
                newState.isValidStartDate = false;
                props.onValidationErrors([INVALID_START_DATE]);
            }
            else
            {
                newState.isValidStartDate = true;
                if (props.showApplyButton !== true) applyChange(changedDate, state.current.selectedEndDate);
            }
        }
        else
        {
            newState.selectedEndDate = changedDate;
            newState.isValidStartDate =true;
            if (moment(changedDate).isSameOrBefore(state.current.selectedStartDate)) {
                newState.isValidEndDate = false;
                props.onValidationErrors([INVALID_END_DATE]);
            }
            else
            {
                newState.isValidEndDate = true;
                if (props.showApplyButton !== true) applyChange(state.current.selectedStartDate, changedDate);
            }
        }
        state.current = newState;
    }

    function applyChange(start: Date, end: Date) {
        props.onRangeChanged(start ?? props.startDate, end ?? props.endDate);
    }

    function getDateFormat(): string {
        return props.showTime ? "MM/dd/yyyy h:mm aa" : "MM/dd/yyyy";
    }

    return (
        <div className={classNames("dateRangePicker", {inline: props.inline})}>
            <div className="start">
                {props.showLabels && <label>{localize("DateRangePicker_Label_StartDate")}</label>}
                <DatePicker className={classNames({ invalid: !state.current.isValidStartDate})} 
                    selected={state.current.selectedStartDate ?? props.startDate}
                    minDate={props.minStartDate ?? new Date(2000, 0)}
                    maxDate={props.maxStartDate}
                    showTimeSelect={props.showTime}
                    dateFormat={getDateFormat()}
                    onChange={(date: Date) => validateDateFilter(date, true)}
                    disabled={props.readOnly} />
            </div>

            {!props.showLabels && <span className="dateRangePickerSpan">to</span>}

            <div className="end">
                {props.showLabels && <label>{localize("DateRangePicker_Label_EndDate")}</label>}
                <DatePicker className={classNames({ invalid: !state.current.isValidEndDate})} 
                    selected={state.current.selectedEndDate ?? props.endDate}
                    minDate={props.minEndDate ?? new Date(2000, 0)}
                    maxDate={props.maxEndDate}
                    showTimeSelect={props.showTime}
                    dateFormat={getDateFormat()}
                    onChange={(date: Date) => validateDateFilter(date, false)}
                    disabled={props.readOnly} />
            </div>

            {props.showApplyButton && 
                <Button className="ml-2" 
                    onClick={() => applyChange(state.current.selectedStartDate, state.current.selectedEndDate)} 
                    disabled={(state.current.selectedStartDate == null && state.current.selectedEndDate == null) || props.readOnly}>
                {localize("DateRangePicker_Button_Apply")}
            </Button>}
        </div>
    );
};

export default DateRangePicker;